<script setup lang="ts">
import { useAuthStore } from '@/stores/auth';
import { useRouter } from 'vue-router';

interface Props {
  message?: string;
}

const props = withDefaults(defineProps<Props>(), {
  message: "L'application est temporairement indisponible pour maintenance. Veuillez réessayer ultérieurement."
});

const authStore = useAuthStore();
const router = useRouter();

const handleLogout = async () => {
  await authStore.logout();
  router.push('/login');
};

const handleRefresh = () => {
  window.location.reload();
};
</script>

<template>
  <div class="app-locked-overlay">
    <div class="app-locked-container">
      <div class="app-locked-icon">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <rect
            x="3"
            y="11"
            width="18"
            height="11"
            rx="2"
            ry="2"
          />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
      </div>
      <h1 class="app-locked-title">
        Application indisponible
      </h1>
      <p class="app-locked-message">
        {{ message }}
      </p>
      
      <div class="app-locked-actions">
        <button class="app-locked-btn app-locked-btn-primary" @click="handleRefresh">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="btn-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="23 4 23 10 17 10" />
            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
          </svg>
          Réessayer
        </button>
        <button class="app-locked-btn app-locked-btn-secondary" @click="handleLogout">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="btn-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16 17 21 12 16 7" />
            <line
              x1="21"
              y1="12"
              x2="9"
              y2="12"
            />
          </svg>
          Se déconnecter
        </button>
      </div>
      
      <div class="app-locked-footer">
        <img src="/images/logo-agco.svg" alt="AGCO" class="app-locked-logo">
      </div>
    </div>
  </div>
</template>

<style scoped>
.app-locked-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99999;
  padding: 20px;
}

.app-locked-container {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  padding: 60px 40px;
  max-width: 500px;
  width: 100%;
  text-align: center;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

.app-locked-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 30px;
  background: linear-gradient(135deg, #B70018 0%, #8B0000 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.app-locked-icon .icon {
  width: 40px;
  height: 40px;
  color: white;
}

.app-locked-title {
  font-size: 28px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 20px;
}

.app-locked-message {
  font-size: 16px;
  color: #4a5568;
  line-height: 1.6;
  margin: 0 0 30px;
  white-space: pre-line;
}

.app-locked-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-bottom: 30px;
}

.app-locked-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
}

.app-locked-btn .btn-icon {
  width: 18px;
  height: 18px;
}

.app-locked-btn-primary {
  background: linear-gradient(135deg, #B70018 0%, #8B0000 100%);
  color: white;
}

.app-locked-btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(183, 0, 24, 0.4);
}

.app-locked-btn-secondary {
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.app-locked-btn-secondary:hover {
  background: #e2e8f0;
}

.app-locked-footer {
  padding-top: 30px;
  border-top: 1px solid #e2e8f0;
}

.app-locked-logo {
  height: 40px;
  opacity: 0.7;
}

@media (max-width: 480px) {
  .app-locked-container {
    padding: 40px 20px;
  }

  .app-locked-title {
    font-size: 24px;
  }

  .app-locked-message {
    font-size: 14px;
  }
  
  .app-locked-actions {
    flex-direction: column;
  }
  
  .app-locked-btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
